import type { QueryFunction } from "@tanstack/react-query";
import type { z } from "zod/v4";
import type { DirEndpoint } from "./endpoint";

export interface DirQueryKey {
  feature: "dir";
  resource: string;
}

export interface DirApiConfig {
  authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response>;
  directoryBaseUrl: string;
}

interface QueryOptions<TResponse> {
  queryKey: readonly [DirQueryKey];
  queryFn: QueryFunction<TResponse, readonly [DirQueryKey]>;
}

export function queryFor<TResponse extends z.ZodType>(endpoint: DirEndpoint<TResponse>) {
  type Response = z.output<TResponse>;

  const queryKey: DirQueryKey = { feature: "dir", resource: endpoint.resource };

  return (config: DirApiConfig): QueryOptions<Response> => {
    const { authenticatedFetch, directoryBaseUrl } = config;

    const queryFn: QueryFunction<Response, readonly [DirQueryKey]> = async ({ signal }) => {
      const response = await authenticatedFetch(`${directoryBaseUrl}${endpoint.path}`, { signal });
      if (!response.ok) {
        throw new Error(`Failed to fetch ${endpoint.resource}: ${String(response.status)}`);
      }
      const data: unknown = await response.json();
      return endpoint.responseSchema.parse(data);
    };

    return { queryKey: [queryKey], queryFn };
  };
}
